import type { CardData, PostIt, Project } from '../types';

export interface ProjectMapSnapshot {
  cardsAtual: CardData[];
  cardsFuturo: CardData[];
  postItsAtual: PostIt[];
  postItsFuturo: PostIt[];
  /** Cards unificados para exibição (atual + futuro, deduplicados por id). */
  allCards: CardData[];
  allPostIts: PostIt[];
  processCount: number;
  elementCount: number;
  completedStages: string[];
  stage: string | null;
}

function collectCardsFromState(state: Project['estado_atual'] | Project['estado_futuro'] | null | undefined): CardData[] {
  if (!state) return [];
  const fromSheets = (state.sheets ?? []).flatMap((sheet) => sheet.cards ?? []);
  const legacy = state.cards ?? [];
  const byId = new Map<string, CardData>();
  for (const card of [...fromSheets, ...legacy]) {
    if (card?.id) byId.set(card.id, card);
  }
  return Array.from(byId.values());
}

function collectPostItsFromState(state: Project['estado_atual'] | Project['estado_futuro'] | null | undefined): PostIt[] {
  if (!state) return [];
  return state.postIts ?? [];
}

function countProcessCards(cards: CardData[]): number {
  return cards.filter((card) => {
    const tipo = card.tipo;
    const vsmIcon = (card as { vsmIcon?: string }).vsmIcon;
    return tipo === 'atividade' || tipo === 'acao' || vsmIcon === 'processo';
  }).length;
}

/** Extrai conteúdo VSM persistido no JSONB do projeto (estado atual + futuro). */
export function extractProjectMapSnapshot(project: Project): ProjectMapSnapshot {
  const cardsAtual = collectCardsFromState(project.estado_atual);
  const cardsFuturo = collectCardsFromState(project.estado_futuro);
  const postItsAtual = collectPostItsFromState(project.estado_atual);
  const postItsFuturo = collectPostItsFromState(project.estado_futuro);

  const cardById = new Map<string, CardData>();
  for (const card of [...cardsAtual, ...cardsFuturo]) {
    if (card?.id) cardById.set(card.id, card);
  }
  const allCards = Array.from(cardById.values());

  const postItById = new Map<string, PostIt>();
  for (const postIt of [...postItsAtual, ...postItsFuturo]) {
    if (postIt?.id) postItById.set(postIt.id, postIt);
  }
  const allPostIts = Array.from(postItById.values());

  const completedStages = project.estado_atual?.completedStages ?? [];
  const stage = project.estado_atual?.stage ?? null;

  return {
    cardsAtual,
    cardsFuturo,
    postItsAtual,
    postItsFuturo,
    allCards,
    allPostIts,
    processCount: countProcessCards(allCards),
    elementCount: allCards.length + allPostIts.length,
    completedStages,
    stage,
  };
}

/** Indica se o projeto possui qualquer conteúdo de processo/VSM modelado. */
export function projectHasModeledProcess(
  snapshot: ProjectMapSnapshot,
  blueprintCount = 0,
): boolean {
  if (blueprintCount > 0) return true;
  if (snapshot.allCards.length > 0) return true;
  if (snapshot.allPostIts.length > 0) return true;
  return false;
}
